import { API_BASE_URL } from '../Utils/constants';
import { getValidIdToken, refreshAuthSession } from '../Access/services/authService';

/**
 * Política de Bearer del API, compartida por `httpClient` y `authFetch`:
 * id token renovado si está por vencer (`getValidIdToken`), sin `Authorization`
 * cuando no hay sesión (nunca `Bearer null`) y, ante un 401 de una request que
 * llevó Bearer, **un** refresh + **un** reintento.
 *
 * @see docs/auth/cognito-session.md
 */

/**
 * Headers con el Bearer del API. Con token, pisa cualquier `Authorization` del
 * consumidor; sin token no agrega nada (nunca `Bearer null`) y respeta un
 * header manual (p. ej. feedback con access token y `auth: false`).
 *
 * @param {object} [headers] - Headers planos del consumidor.
 * @param {string|null} [bearer] - Id token.
 * @returns {object}
 */
export const withBearer = (headers, bearer) => {
  const result = { ...(headers || {}) };
  if (!bearer) return result;
  Object.keys(result).forEach((key) => {
    if (key.toLowerCase() === 'authorization') delete result[key];
  });
  result.Authorization = `Bearer ${bearer}`;
  return result;
};

/**
 * Tras un 401 con Bearer: renueva y devuelve el token nuevo, o `null` si no
 * corresponde reintentar (refresh muerto, falla de red, o el token no cambió).
 * Un refresh muerto ya dispara el flujo de sesión vencida en `authService`.
 *
 * @param {string} staleBearer - Token que recibió el 401.
 * @returns {Promise<string|null>}
 */
export const refreshBearerAfter401 = async (staleBearer) => {
  const result = await refreshAuthSession({ staleIdToken: staleBearer });
  if (result.status !== 'refreshed' && result.status !== 'unchanged') return null;
  return result.idToken && result.idToken !== staleBearer ? result.idToken : null;
};

const resolveApiUrl = (url) => {
  const value = String(url);
  if (/^https?:\/\//i.test(value)) return value;
  return `${API_BASE_URL}${value.startsWith('/') ? value : `/${value}`}`;
};

/**
 * `fetch` contra el API con la política de Bearer del `httpClient`, pero
 * devolviendo el `Response` crudo: lo usan los servicios previos al cliente
 * para no cambiar sus contratos (JSON crudo, envelopes propios, servicios que
 * lanzan). Sin timeout propio ni reintento ante 429 (igual que antes).
 *
 * No usar para URLs prefirmadas de S3: van con `fetch` directo, sin Bearer.
 *
 * @param {string} url - URL absoluta del API o ruta relativa a `API_BASE_URL`.
 * @param {RequestInit} [init] - Opciones de `fetch`; `headers` como objeto plano.
 *   El body debe poder reenviarse (string / FormData / Blob) por el reintento.
 * @param {object} [options]
 * @param {boolean} [options.auth=true] - Adjunta el Bearer si hay sesión.
 * @param {boolean} [options.retryOn401=true] - Con Bearer, un refresh + un
 *   reintento ante 401.
 * @returns {Promise<Response>}
 */
export const authFetch = async (url, init = {}, { auth = true, retryOn401 = true } = {}) => {
  const target = resolveApiUrl(url);
  const { signal } = init;
  const send = (bearer) => fetch(target, { ...init, headers: withBearer(init.headers, bearer) });

  const bearer = auth ? await getValidIdToken() : null;
  const response = await send(bearer);

  if (response.status === 401 && retryOn401 && bearer && !signal?.aborted) {
    const nextBearer = await refreshBearerAfter401(bearer);
    if (nextBearer && !signal?.aborted) return send(nextBearer);
  }
  return response;
};
